"use client";

import { cn, str } from "@/lib/utils";
import { ProviderType } from "@/types";
import { useRouter } from "next/router";
import { Template } from "@/types/template";
import React, { forwardRef, useRef } from "react";
import { getProviderMeta } from "@/lib/provider/meta";
import { AnimatedBeam } from "@/components/ui/animated-beam";
import { Server } from "lucide-react";
import { Icon } from "@/components/landing/Navbar";

const Circle = forwardRef<HTMLDivElement, { className?: string; children?: React.ReactNode }>(
  ({ className, children }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "z-10 flex size-12 items-center justify-center rounded-full border-2 border-border bg-white p-3 shadow-[0_0_20px_-12px_rgba(0,0,0,0.8)] text-black",
          className
        )}
      >
        {children}
      </div>
    );
  }
);

Circle.displayName = "Circle";

export const Launch = ({ template }: { template: Template }) => {
  const router = useRouter();

  if (!template) {
    return null;
  }

  const hostId = str(router.query.hostId);
  const hostname = str(router.query.hostname);

  return (
    <AnimatedBeamMultipleOutputDemo template={template} hostname={hostname} hostId={hostId} />
  );
};

export function AnimatedBeamMultipleOutputDemo({
  className,
  template,
  hostname,
  hostId
}: {
  className?: string;
  template: Template;
  hostname?: string;
  hostId?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const div1Ref = useRef<HTMLDivElement>(null);
  const div2Ref = useRef<HTMLDivElement>(null);
  const div3Ref = useRef<HTMLDivElement>(null);
  const div4Ref = useRef<HTMLDivElement>(null);
  const div5Ref = useRef<HTMLDivElement>(null);

  const router = useRouter();
  const provider = (router.query.provider as ProviderType) ?? "do";
  const meta = getProviderMeta(provider);

  const Icons = {
    logoProvider: () => <img src={meta?.logoUrl} alt={meta?.name} />,
    hostExisting: () => <Server className="h-10 w-10" />,
    hostNew: () => <Server className="h-10 w-10" />,
    logoTemplate: () => (
      <img src={template?.logo} alt={template?.name} className="rounded-md" />
    )
  };

  return (
    <div
      className={cn(
        "relative flex h-[60px] w-full items-center justify-center overflow-hidden pr-10",
        className
      )}
      ref={containerRef}
    >
      <div className="flex size-full max-w-lg flex-row items-stretch justify-between gap-10">
        <div className="flex flex-col justify-center gap-2">
          <Circle ref={div1Ref}>
            <Icon dark />
          </Circle>
        </div>
        <div className="flex flex-col justify-center">
          {hostId ? (
            <Circle ref={div2Ref}>
              <Icons.hostExisting />
            </Circle>
          ) : hostname ? (
            <Circle ref={div3Ref}>
              <Icons.hostNew />
            </Circle>
          ) : (
            <Circle ref={div4Ref}>
              <Icons.logoProvider />
            </Circle>
          )}
        </div>
        <div className="flex flex-col justify-center">
          <Circle ref={div5Ref}>
            <Icons.logoTemplate />
          </Circle>
        </div>
      </div>

      <AnimatedBeam containerRef={containerRef} fromRef={div1Ref} toRef={div4Ref} />
      <AnimatedBeam containerRef={containerRef} fromRef={div4Ref} toRef={div5Ref} />

      {hostId ? (
        <>
          <AnimatedBeam containerRef={containerRef} fromRef={div1Ref} toRef={div2Ref} />
          <AnimatedBeam containerRef={containerRef} fromRef={div2Ref} toRef={div5Ref} />
        </>
      ) : hostname ? (
        <>
          <AnimatedBeam containerRef={containerRef} fromRef={div1Ref} toRef={div3Ref} />
          <AnimatedBeam containerRef={containerRef} fromRef={div3Ref} toRef={div5Ref} />
        </>
      ) : null}
    </div>
  );
}

export default AnimatedBeamMultipleOutputDemo;
